import { useIntl } from 'react-intl';

import { useErrorToast } from '~/common/errors/useErrorToast';
import { useToast } from '~/eds/src/components/hooks/useToast';
import { Modal } from '~/eds/src/components/Modal/Modal';
import { Text } from '~/eds/src/components/Text/Text';
import { EsignatureType } from '~/enums/EsignatureType';
import { api } from '~/redux/api/createApi';

import { EsignatureIntegrationType } from '../../types';

export interface ConfirmUpdateModalProps {
  isVisible: boolean;
  integrationType: EsignatureIntegrationType;
  providerName: EsignatureType;
  integrationId: string;
  onHide?: () => void;
}

export const ConfirmUpdateModal = ({
  isVisible,
  integrationType,
  providerName,
  integrationId,
  onHide = () => {},
}: ConfirmUpdateModalProps) => {
  const intl = useIntl();
  const { toast } = useToast();
  const { toast: errorToast } = useErrorToast();

  const [updateIntegration, { isLoading: isUpdateLoading }] =
    api.endpoints.updateEsignatureIntegration.useMutation();

  const [getAuthenticate, { isLoading: isAuthenticateLoading }] =
    api.endpoints.getAuthenticate.useLazyQuery();

  const isLoading = isUpdateLoading || isAuthenticateLoading;

  const getConfirmUpdateTitle = () => {
    return integrationType === 'company'
      ? intl.formatMessage({
          id: 'NewESignature.ESignatureIntegrationCard.ConfirmUpdateCompanyTitle',
          defaultMessage: 'Update to Company account',
          description:
            'Title for company integration update confirmation modal',
        })
      : intl.formatMessage({
          id: 'NewESignature.ESignatureIntegrationCard.ConfirmUpdatePersonalTitle',
          defaultMessage: 'Update to Personal account',
          description:
            'Title for personal integration update confirmation modal',
        });
  };

  const getConfirmUpdateMessage = () => {
    return integrationType === 'company'
      ? intl.formatMessage(
          {
            id: 'NewESignature.ESignatureIntegrationCard.ConfirmUpdateCompanyMessage',
            defaultMessage:
              'Updating to Company account requires the Administrator to authenticate with {providerName}. After, all users can collect e-signatures without authenticating. Signers will receive emails from one authenticated account. Documents that have not been fully signed will need to restart the signature process.',
            description:
              'Message for company integration update confirmation. Example: providerName = "DocuSign"',
          },
          { providerName },
        )
      : intl.formatMessage(
          {
            id: 'NewESignature.ESignatureIntegrationCard.ConfirmUpdatePersonalMessage',
            defaultMessage:
              'Updating to Personal account will require each user to authenticate with {providerName}. Signers will receive emails from the user’s account. Documents that have not been fully signed will need to restart the signature process.',
            description:
              'Message for personal integration update confirmation. Example: providerName = "DocuSign"',
          },
          { providerName },
        );
  };

  const handleConfirm = async () => {
    try {
      if (integrationType === 'company') {
        const [, authResponse] = await Promise.all([
          updateIntegration({
            integrationId,
            integrationType,
          }).unwrap(),
          getAuthenticate({
            return_to: window.location.href,
          }).unwrap(),
        ]);

        if (authResponse?.authenticateUrl) {
          window.location.href = authResponse.authenticateUrl;
          return;
        }
      } else {
        await updateIntegration({
          integrationId,
          integrationType,
        }).unwrap();

        onHide();

        toast({
          message: intl.formatMessage({
            id: 'NewESignature.ESignatureIntegrationCard.UpdateSuccess',
            defaultMessage: 'Your authentication account was updated.',
            description: 'Success message when integration is updated',
          }),
          status: 'success',
          autoDismiss: true,
        });
      }
    } catch (error) {
      errorToast(error instanceof Error ? error : new Error(String(error)));
    }
  };

  return (
    <Modal
      isVisible={isVisible}
      primaryAction={{
        text: intl.formatMessage(
          {
            id: 'NewESignature.ESignatureIntegrationCard.ContinueToProvider',
            defaultMessage: 'Continue to {providerName}',
            description:
              'Continue button text with provider name. Example: providerName = "Document Name"',
          },
          { providerName },
        ),
        variant: 'primary',
        onClick: handleConfirm,
        isLoading,
      }}
      title={getConfirmUpdateTitle()}
      onHide={onHide}
    >
      <Text>{getConfirmUpdateMessage()}</Text>
    </Modal>
  );
};
